import { useEffect, useRef, useState } from 'react';
import type { StepProps } from '../engine/context.ts';
import { TitleBlock } from '../ui/TitleBlock.tsx';
import { Icon } from '../ui/Icon.tsx';
import { CEL, CREAM, INK, LINE, MUTED, PAPER, SHADOW_FLOAT, VIOLET, ORANGE, MOTION, LAVENDER } from '../ui/theme.ts';
import { haptic } from '../engine/haptics.ts';

const DRAG_THRESHOLD = 80;

/** Pointer drag + two buttons. Answer: { yes:[i], no:[i], count }. */
export function SwipeCards({ step, content, ctx }: StepProps) {
  const key = step.answerKey!;
  const cards = content.cards ?? [];
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState<'yes' | 'no' | null>(null);
  const [flying, setFlying] = useState(false);
  const [dx, setDx] = useState(0);
  const result = useRef<{ yes: number[]; no: number[] }>({ yes: [], no: [] });
  const drag = useRef<{ id: number; x0: number; t0: number } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    ctx.setCta({ label: null });
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [step.id]);

  const commit = (choice: 'yes' | 'no') => {
    if (flying) return;
    haptic();
    setDir(choice);
    setFlying(true);
    result.current[choice].push(index);
    timer.current = setTimeout(() => {
      const next = index + 1;
      const { yes, no } = result.current;
      ctx.setAnswer(key, { yes: [...yes], no: [...no], count: yes.length + no.length });
      if (next >= cards.length) {
        ctx.event('swipe_done', { yes_count: yes.length });
        ctx.next();
      } else {
        setIndex(next);
        setDir(null);
        setFlying(false);
        setDx(0);
      }
    }, MOTION.swipeFly);
  };

  const onDown = (e: React.PointerEvent) => {
    if (flying) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    drag.current = { id: e.pointerId, x0: e.clientX, t0: Date.now() };
  };
  const onMove = (e: React.PointerEvent) => {
    if (!drag.current || drag.current.id !== e.pointerId) return;
    const delta = e.clientX - drag.current.x0;
    setDx(delta);
    setDir(Math.abs(delta) < 24 ? null : delta > 0 ? 'yes' : 'no');
  };
  const onUp = (e: React.PointerEvent) => {
    if (!drag.current || drag.current.id !== e.pointerId) return;
    const delta = e.clientX - drag.current.x0;
    const velocity = Math.abs(delta) / Math.max(1, Date.now() - drag.current.t0);
    drag.current = null;
    if (Math.abs(delta) > DRAG_THRESHOLD || velocity > 0.6) commit(delta > 0 ? 'yes' : 'no');
    else {
      setDx(0);
      setDir(null);
    }
  };

  return (
    <>
      <TitleBlock label={content.label} title={content.title} highlight={content.highlight} subtitle={content.subtitle} />

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18 }}>
        <div style={{ position: 'relative', width: '100%', height: 300, flex: 'none' }}>
          {cards.map((card, i) => {
            const rel = i - index;
            if (rel < 0 || rel > 2) return null;
            const top = rel === 0;
            const tilt = top && dx ? `translate(${dx}px,${-Math.abs(dx) * 0.12}px) rotate(${dx * 0.05}deg)` : undefined;
            return (
              <div
                key={i}
                onPointerDown={top ? onDown : undefined}
                onPointerMove={top ? onMove : undefined}
                onPointerUp={top ? onUp : undefined}
                onPointerCancel={top ? onUp : undefined}
                style={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: 28,
                  background: top && dir === 'yes' ? `linear-gradient(135deg,${LAVENDER}33,${PAPER})` : top && dir === 'no' ? `linear-gradient(135deg,${CREAM},${PAPER})` : PAPER,
                  boxShadow: SHADOW_FLOAT,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  padding: 28,
                  textAlign: 'center',
                  touchAction: 'none',
                  cursor: top ? 'grab' : 'default',
                  transform:
                    tilt ??
                    (rel === 0
                      ? dir === 'yes'
                        ? 'translate(48px,-14px) rotate(9deg)'
                        : dir === 'no'
                          ? 'translate(-48px,-14px) rotate(-9deg)'
                          : 'none'
                      : `scale(${rel === 1 ? 0.96 : 0.92}) translateY(${rel === 1 ? 8 : 16}px)`),
                  zIndex: 10 - rel,
                  transition: dx ? 'none' : 'transform .2s',
                  animation: top && flying ? `${dir === 'no' ? 'fk-fly-l' : 'fk-fly'} ${MOTION.swipeFly}ms ease-in forwards` : 'none',
                }}
              >
                <span style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-.02em', lineHeight: 1.2 }}>{card.text}</span>
                {card.caption ? <span style={{ fontSize: 13, color: MUTED, fontWeight: 600 }}>{card.caption}</span> : null}
                {top && dir === 'yes' ? (
                  <span style={{ position: 'absolute', right: 14, top: 14, padding: '8px 14px', borderRadius: 999, background: CEL, color: '#fff', fontWeight: 800, fontSize: 14, transform: 'rotate(8deg)', boxShadow: `0 10px 30px ${VIOLET}59` }}>
                    {ctx.shared.yesLabel}
                  </span>
                ) : null}
                {top && dir === 'no' ? (
                  <span style={{ position: 'absolute', left: 14, top: 14, padding: '8px 14px', borderRadius: 999, background: INK, color: '#fff', fontWeight: 800, fontSize: 14, transform: 'rotate(-8deg)', boxShadow: '0 10px 30px rgba(31,27,45,.25)' }}>
                    {ctx.shared.noLabel}
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, width: '100%' }}>
          <button
            type="button"
            onClick={() => commit('no')}
            style={{ minHeight: 56, borderRadius: 999, border: `2px solid ${LINE}`, background: PAPER, cursor: 'pointer', color: INK, fontWeight: 800, fontSize: 15, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          >
            <Icon name="arrow-left" size={20} color={MUTED} />
            {ctx.shared.noLabel}
          </button>
          <button
            type="button"
            onClick={() => commit('yes')}
            style={{ minHeight: 56, borderRadius: 999, border: 0, background: CEL, cursor: 'pointer', color: '#fff', fontWeight: 800, fontSize: 15, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: `0 10px 30px ${VIOLET}4d` }}
          >
            {ctx.shared.yesLabel}
            <Icon name="arrow-right" size={20} />
          </button>
        </div>

        <div style={{ display: 'flex', gap: 6 }}>
          {cards.map((_, i) => (
            <span key={i} style={{ width: 8, height: 8, borderRadius: '50%', background: i < index ? VIOLET : i === index ? ORANGE : LINE }} />
          ))}
        </div>
      </div>
    </>
  );
}
